import type { TableColumn } from "../../../models/ManualAuthoring";

type WidthValidationResult = {
  isValid: boolean;
  errors: string[];
};

export function findColumnById(
  columns: TableColumn[],
  id: string
): TableColumn | null {
  for (const col of columns) {
    if (col.id === id) return col;

    if (col.columns?.length) {
      const found = findColumnById(col.columns, id);
      if (found) return found;
    }
  }

  return null;
}

function formatWidth(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function buildGroupLabel(path: string[]): string {
  if (path.length === 0) return "Top-level columns";
  return path.join(" > ");
}

function isValidPercent(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 100;
}

function validateGroup(
  columns: TableColumn[],
  path: string[],
  errors: string[],
  epsilon = 0.01
): void {
  if (!columns.length) return;

  let sum = 0;

  columns.forEach((column, index) => {
    const width = Number(column.width);
    const label = column.header?.trim() || `Column ${index + 1}`;

    if (!isValidPercent(width)) {
      errors.push(
        `${buildGroupLabel(path)}: "${label}" must have a width between 0 and 100.`
      );
    } else {
      sum += width;
    }

    if (column.columns?.length) {
      validateGroup(column.columns, [...path, label], errors, epsilon);
    }
  });

  if (Math.abs(sum - 100) > epsilon) {
    errors.push(
      `${buildGroupLabel(path)} total width must be 100% (current: ${formatWidth(sum)}%).`
    );
  }
}

export function validateColumnWidthPercentages(
  columns: TableColumn[]
): WidthValidationResult {
  const errors: string[] = [];
  validateGroup(columns, [], errors);

  return {
    isValid: errors.length === 0,
    errors,
  };
}
